import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { getDisplayName } from '../../../../models/NameUtils';
import { InputGroupAddon, InputGroupInput, InputGroupV2 } from '../../../components-v2/InputGroup';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
} from '../../../components-v2/Combobox';
import { TabToggle } from '../../../components-v2/TabToggle';
import { THEME } from '../../../components-v2/Theme/theme';
import { CssSelectors } from '../../../styles/styles';
import { SearchIcon } from '../../../components/svgs/icons/SearchIcon';
import { ContactFilter } from './ContactFilter';

export const ACTION_ITEMS_VIEWS = ['list', 'calendar', 'completed'] as const;
export type ActionItemsView = (typeof ACTION_ITEMS_VIEWS)[number];

interface IProps {
	scope: string;
	onScopeChange: (scope: string) => void;
	users: Api.IUser[];
	currentUserId: string | undefined;
	search: string;
	onSearchChange: (value: string) => void;
	searchInputRef: React.RefObject<HTMLInputElement>;
	contactId: string | null;
	onContactChange: (contact: Api.IContact | null) => void;
	impersonationContext?: Api.IImpersonationContext;
	view: ActionItemsView;
	onViewChange: (view: ActionItemsView) => void;
}

interface IScopeOption {
	value: string;
	label: string;
}

export function buildScopeOptions(users: Api.IUser[], currentUserId: string | undefined): IScopeOption[] {
	const teammates = users
		.filter(user => user.id !== currentUserId)
		.map(user => ({ value: user.id, label: getDisplayName(user) }))
		.sort((a, b) => a.label.localeCompare(b.label));
	return [{ value: 'mine', label: 'My action items' }, { value: 'everyone', label: 'Everyone' }, ...teammates];
}

export function scopeLabel(scope: string, users: Api.IUser[]): string {
	if (scope === 'mine') {
		return 'My action items';
	}
	if (scope === 'everyone') {
		return 'Everyone';
	}
	const user = users.find(x => x.id === scope);
	return user ? getDisplayName(user) : 'Teammate';
}

export const ActionItemsHeader: React.FC<IProps> = ({
	scope,
	onScopeChange,
	users,
	currentUserId,
	search,
	onSearchChange,
	searchInputRef,
	contactId,
	onContactChange,
	impersonationContext,
	view,
	onViewChange,
}) => {
	const scopeOptions = buildScopeOptions(users, currentUserId);
	const selectedScope = scopeOptions.find(option => option.value === scope) ?? scopeOptions[0];
	const [scopeOpen, setScopeOpen] = React.useState(false);
	const [scopeQuery, setScopeQuery] = React.useState('');
	return (
		<div className={css(styleSheet.header)}>
			<div className={css(styleSheet.left)}>
				<Combobox
					items={scopeOptions}
					value={selectedScope}
					onValueChange={option => option && onScopeChange((option as IScopeOption).value)}
					itemToStringLabel={option => (option as IScopeOption).label}
					isItemEqualToValue={(item, value) => (item as IScopeOption)?.value === (value as IScopeOption)?.value}
					open={scopeOpen}
					onOpenChange={open => {
						setScopeOpen(open);
						setScopeQuery('');
					}}
					inputValue={scopeOpen ? scopeQuery : selectedScope.label}
					onInputValueChange={value => setScopeQuery(value)}
				>
					<ComboboxInput
						aria-label='Scope'
						placeholder={scopeOpen ? 'Search teammates' : 'Whose action items'}
						styles={[styleSheet.scope]}
						onFocus={event => event.currentTarget.select()}
					/>
					<ComboboxContent>
						<ComboboxEmpty>No teammates match</ComboboxEmpty>
						<ComboboxList>
							{(option: IScopeOption) => (
								<ComboboxItem key={option.value} value={option}>
									<ComboboxItemRadioIndicator />
									{option.label}
								</ComboboxItem>
							)}
						</ComboboxList>
					</ComboboxContent>
				</Combobox>
				<ContactFilter contactId={contactId} onChange={onContactChange} impersonationContext={impersonationContext} />
				<InputGroupV2 styles={[styleSheet.search]}>
					<InputGroupAddon>
						<SearchIcon />
					</InputGroupAddon>
					<InputGroupInput
						ref={searchInputRef}
						aria-label='Search action items'
						placeholder='Search'
						value={search}
						onChange={event => onSearchChange(event.currentTarget.value)}
						onKeyDown={event => {
							if (event.key === 'Escape') {
								onSearchChange('');
								event.currentTarget.blur();
							}
						}}
					/>
				</InputGroupV2>
			</div>
			<div className={css(styleSheet.right)}>
				<TabToggle
					styles={[styleSheet.viewToggle]}
					value={view}
					onValueChange={value => onViewChange(value as ActionItemsView)}
					items={[
						{ value: 'list', label: 'List' },
						{ value: 'calendar', label: 'Calendar' },
						{ value: 'completed', label: 'Completed' },
					]}
				/>
			</div>
		</div>
	);
};

const styleSheet = StyleSheet.create({
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: THEME.spacing.md,
		flexWrap: 'wrap',
		padding: `${THEME.spacing.md}px 0 ${THEME.spacing.sm}px`,
	},
	left: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		flex: 1,
		minWidth: 0,
		flexWrap: 'wrap',
	},
	right: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	// TabToggle is the only control on this row that derives its height from padding and
	// line-height instead of declaring one, so it lands taller than the 36px search input and
	// dropdowns beside it. Pin it here rather than in the primitive, which has twelve consumers.
	viewToggle: {
		height: 36,
		...CssSelectors.allDescendants({ paddingBottom: 0, paddingTop: 0 }, '[data-slot="tab-toggle-item"]'),
	},
	scope: {
		width: 220,
	},
	search: {
		flex: 1,
		minWidth: 180,
		maxWidth: 360,
	},
});
